import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import type { ColDef } from 'ag-grid-community';
import {
  IconButton,
  UniversalModuleView,
} from '../../../ui-components/listing';
import Dropdown from '../../../ui-components/Dropdown';
import { useGetEventListQuery } from './eventsApi';
import type { Event as AppEvent } from './model/event';
import StatusTag from '../../../ui-components/StatusTag';

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [refreshToken, setRefreshToken] = React.useState(0);
  const [selectedStatus, setSelectedStatus] = React.useState<string>('ALL');

  const { refetch } = useGetEventListQuery({ page: 1, limit: 10 });

  const handleRefresh = useCallback(() => {
    refetch();
    setRefreshToken((prev) => prev + 1);
  }, [refetch]);

  // ── Column definitions ──────────────────────────────────────────────────────────
  const columns = useMemo<ColDef<AppEvent>[]>(
    () => [
      {
        headerName: 'EVENT ID',
        field: 'eventId',
        sortable: true,
        minWidth: 120,
        cellClass: 'font-medium',
      },
      {
        headerName: 'EVENT NAME',
        field: 'eventName',
        sortable: true,
        minWidth: 200,
        cellClass: 'font-medium text-brand-600 cursor-pointer hover:underline',
        cellRenderer: (params: any) => (
          <span onClick={() => navigate(`/events/view/${params.data.id}`)}>
            {params.value}
          </span>
        ),
      },
      {
        headerName: 'HOST NAME',
        field: 'hostName',
        sortable: true,
        minWidth: 160,
      },
      {
        headerName: 'START DATE',
        field: 'startDate',
        sortable: true,
        minWidth: 140,
      },
      {
        headerName: 'EXPIRY DATE',
        field: 'expiryDate',
        sortable: true,
        minWidth: 140,
      },
      {
        headerName: 'STATUS',
        field: 'status',
        minWidth: 120,
        cellRenderer: (params: any) => {
          return <StatusTag value={params.value} />;
        },
      },
      {
        headerName: 'CREATED DATE',
        field: 'createdDate',
        sortable: true,
        minWidth: 140,
      },
    ],
    [navigate]
  );

  // ── Row renderer ─────────────────────────────────────────────────────────────
  const renderTableRow = useCallback(
    (row: AppEvent): React.ReactNode[] => [
      <span key="eventId" className="text-sm font-medium">
        {row.eventId}
      </span>,
      <span
        key="eventName"
        className="font-medium text-brand-600 truncate cursor-pointer hover:underline"
        onClick={() => navigate(`/events/view/${row.id}`)}
      >
        {row.eventName}
      </span>,
      <span
        key="hostName"
        className="text-sm text-neutral-600 dark:text-neutral-400 truncate"
      >
        {row.hostName}
      </span>,
      <span
        key="startDate"
        className="text-sm text-neutral-600 dark:text-neutral-400"
      >
        {row.startDate}
      </span>,
      <span
        key="expiryDate"
        className="text-sm text-neutral-600 dark:text-neutral-400"
      >
        {row.expiryDate}
      </span>,
      <span key="status" className="flex items-center">
        <StatusTag value={row.status} />
      </span>,
      <span
        key="createdDate"
        className="text-sm text-neutral-600 dark:text-neutral-400"
      >
        {row.createdDate}
      </span>,
    ],
    [navigate]
  );

  return (
    <UniversalModuleView<AppEvent>
      moduleKey="events"
      title="Event Management"
      apiEndpoint="api/v1/events/list"
      listMethod="GET"
      columnsConfig={columns}
      rowKey={(row) => row.id}
      tableRowRenderer={renderTableRow}
      refreshToken={refreshToken}
      extraPayload={{
        status: selectedStatus === 'ALL' ? undefined : selectedStatus,
      }}
      hideFilterButton={true}
      extraActions={({ reset }) => (
        <div className="flex items-center gap-2">
          <div className="w-40 mr-1">
            <Dropdown
              value={selectedStatus}
              setValue={(val) => {
                setSelectedStatus(String(val));
                reset();
              }}
              options={[
                { label: 'All Status', value: 'ALL' },
                { label: 'Active', value: 'Active' },
                { label: 'Expired', value: 'Expired' },
              ]}
              placeholder="All Status"
              appendTo="body"
            />
          </div>
          <IconButton
            icon={RefreshCw}
            title="Refresh"
            onClick={() => {
              handleRefresh();
              reset();
            }}
          />
        </div>
      )}
      searchPlaceholder="Search events..."
      note={
        <>
          <strong>Note:</strong> This is a read-only view. Events cannot be
          edited or ended by administrators. Click on any event row to view
          detailed information and RSVP statistics.
        </>
      }
      initialPageSize={10}
      breadcrumbs={[
        { label: 'Sitemap' },
        { label: 'Event Management', to: '/events', current: true },
      ]}
    />
  );
};

export default EventsPage;
